import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellRing, PartyPopper } from "lucide-react";
import { launchLabel, launchOpensAt } from "@/components/LaunchPartyBanner";

const pad = (n) => String(n).padStart(2, "0");

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, (target?.getTime() || 0) - now);
  return { d: Math.floor(ms / 864e5), h: Math.floor(ms / 36e5) % 24, m: Math.floor(ms / 6e4) % 60, s: Math.floor(ms / 1e3) % 60, done: ms === 0 };
};

export default function LaunchCountdown({ party, token, user }) {
  const opens = launchOpensAt(party.release_date);
  const { d, h, m, s, done } = useCountdown(opens);
  const [notified, setNotified] = useState(false);

  // Once the clock hits zero, re-check every 15s — the 8 AM go-live job opens the room.
  useEffect(() => {
    if (!done) return undefined;
    const t = setInterval(() => window.location.reload(), 15000);
    return () => clearInterval(t);
  }, [done]);

  const notify = async () => {
    try {
      await api.post(`/watch-party/${token}/notify-open`);
      setNotified(true);
      toast.success("We'll email you the moment the party opens");
    } catch {
      toast.error("Couldn't save that — try again");
    }
  };

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-6" data-testid="launch-countdown">
      <div className="max-w-lg w-full text-center">
        <PartyPopper className="w-12 h-12 mx-auto text-terracotta mb-4" />
        <p className="text-[11px] uppercase tracking-[0.25em] text-sky-300 font-semibold">Launch Watch Party</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">{party.film_title}</h1>
        <p className="text-sm text-foreground/60 mt-2">
          Hosted by {party.host_name} · opens {launchLabel(party.release_date)} (8:00 AM WAT) on release day
        </p>
        <div className="flex justify-center gap-3 mt-8" data-testid="launch-countdown-clock">
          {done ? (
            <p className="text-gold font-semibold">Opening any moment now…</p>
          ) : (
            [[d, "days"], [h, "hrs"], [m, "min"], [s, "sec"]].map(([v, l]) => (
              <div key={l} className="w-16 sm:w-20 rounded-xl border border-white/10 bg-surface py-3">
                <p className="font-display text-2xl sm:text-3xl font-black tabular-nums">{pad(v)}</p>
                <p className="text-[10px] uppercase tracking-widest text-foreground/50">{l}</p>
              </div>
            ))
          )}
        </div>
        <div className="mt-8">
          {user ? (
            <button
              type="button"
              onClick={notify}
              disabled={notified}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-500 hover:bg-sky-400 disabled:bg-sky-500/20 disabled:text-sky-200 text-white text-sm font-semibold transition-colors"
              data-testid="launch-notify-btn"
            >
              {notified ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              {notified ? "You'll get an email when it opens" : "Email me when it opens"}
            </button>
          ) : (
            <Link to="/login" state={{ from: `/watch-party/${token}` }} className="text-sm text-gold hover:underline" data-testid="launch-login-link">
              Log in to get an email when it opens
            </Link>
          )}
        </div>
        <Link to={`/film/${party.film_id}`} className="block mt-6 text-xs text-foreground/50 hover:text-foreground" data-testid="launch-film-link">
          Watch the trailer while you wait →
        </Link>
      </div>
    </div>
  );
}
